/** Standard product brand — student blue (trust, focus). Used for every role. */
export const BRAND = {
  key: "blue",
  primary: "#2E6BE6",
  primary2: "#3377FF",
  primaryDark: "#2659BF",
  primarySoft: "#EAF0FF",
  primarySoftHover: "#E0EBFF",
  primarySoftActive: "#B6D5FF",
  primaryHover: "#2E6BE6",
  primaryActive: "#2953CC",
  darkHover: "#1F47B9",
  darkActive: "#173673",
  darker: "#122A59",
  rgb: "46,107,230",
};

export const STUDENT_BRAND = BRAND;
/** Kept for older imports; chrome no longer switches to green. */
export const FACULTY_BRAND = BRAND;

export function brandForRole(_role) {
  return BRAND;
}

const NEUTRAL = {
  bg: "#F7F8FA",
  surface: "#FFFFFF",
  surfaceAlt: "#F3F4F6",
  border: "#E8ECF2",
  borderStrong: "#D1D5DB",
  text: "#111827",
  muted: "#6B7280",
  faint: "#9CA3AF",
  error: "#E11D2E",
  errorBg: "#FDE8EA",
  warning: "#1B4F72",
  warningBg: "#E7F0F6",
  ink: "#0B0B0B",
  inkSoft: "#1A1A1A",
  mockColor: "#E11D2E",
  mockBg: "#FDE8EA",
  dtPurple: "#4338CA",
  dtPurpleBg: "#EEF2FF",
};

function brandSlice(brand) {
  return {
    primary: brand.primary,
    primary2: brand.primary2,
    primaryDark: brand.primaryDark,
    primarySoft: brand.primarySoft,
    primaryHover: brand.primaryHover,
    primaryActive: brand.primaryActive,
    primaryRgb: brand.rgb,
    success: brand.primary,
    successBg: brand.primarySoft,
    accent: brand.primary,
    accentSoft: brand.primarySoft,
    tagBg: brand.primarySoft,
    tagText: brand.primaryDark,
    shell: "#FFFFFF",
    shellAlt: "#F7F8FA",
    shellText: "#111827",
    shellMuted: "#6B7280",
    shellActive: brand.primary,
    shellActiveBg: brand.primarySoft,
    shellBorder: "#E8ECF2",
    shellRail: brand.primary,
    auColor: brand.darkActive,
    auBg: brand.primarySoft,
    dtBlue: brand.primary,
    dtBlueBg: brand.primarySoft,
    dtGreen: brand.primaryDark,
    dtGreenBg: brand.primarySoft,
    dtOrange: brand.primary2,
    dtOrangeBg: brand.primarySoft,
  };
}

function tableSlice(brand) {
  const glow = `rgba(${brand.rgb},0.45)`;
  return {
    headBg: "#0B0B0B",
    headText: "#FFFFFF",
    headBorder: glow,
    headAccent: brand.primary,
    editBanner: brand.primarySoft,
    rowAlt: "#F7FAFF",
    rowHover: brand.primarySoft,
    selected: brand.primarySoft,
    selectedBorder: brand.primary,
    week: {
      headBg: "#0B0B0B",
      headBorder: glow,
      accent: brand.primary,
      selected: brand.primarySoft,
      selectedBorder: brand.primary,
    },
    day: {
      headBg: "#0B0B0B",
      headBorder: glow,
      accent: brand.primary,
      selected: brand.primarySoft,
      selectedBorder: brand.primary,
    },
    homework: {
      headBg: "#0B0B0B",
      headBorder: "#1A1A1A",
      accent: brand.primary,
      selected: "#F3F4F6",
      selectedBorder: "#0B0B0B",
    },
  };
}

export const C = {
  ...NEUTRAL,
  ...brandSlice(BRAND),
};

export const TABLE = tableSlice(BRAND);

function writeCssVars(brand) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.roleTheme = brand.key;
  root.style.setProperty("--brand-primary", brand.primary);
  root.style.setProperty("--brand-primary-2", brand.primary2);
  root.style.setProperty("--brand-primary-dark", brand.primaryDark);
  root.style.setProperty("--brand-primary-soft", brand.primarySoft);
  root.style.setProperty("--brand-primary-soft-hover", brand.primarySoftHover);
  root.style.setProperty("--brand-primary-soft-active", brand.primarySoftActive);
  root.style.setProperty("--brand-primary-hover", brand.primaryHover);
  root.style.setProperty("--brand-blue", brand.primary);
  root.style.setProperty("--brand-green", brand.primary);
  root.style.setProperty("--brand-black", "#0B0B0B");
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) themeMeta.setAttribute("content", brand.primary);
}

export function applyBrandPalette(_role) {
  Object.assign(C, brandSlice(BRAND));
  const nextTable = tableSlice(BRAND);
  Object.keys(nextTable).forEach((key) => {
    const val = nextTable[key];
    if (val && typeof val === "object" && !Array.isArray(val)) {
      TABLE[key] = { ...(TABLE[key] || {}), ...val };
    } else {
      TABLE[key] = val;
    }
  });
  writeCssVars(BRAND);
  return BRAND;
}

writeCssVars(BRAND);

export const LAYOUT = {
  navbarHeight: 64,
  sidebarWidth: 256,
  sidebarCollapsedWidth: 72,
  radius: 12,
  radiusSm: 8,
  radiusLg: 18,
};
